// ============================================
// Admin Authentication Service
//
// Firebase Auth + Firestore role check for admin panel.
// Uses admin_users/{uid} collection for role-based access.
// Roles: owner | ceo | builder | medical_director
// ============================================

import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  type User,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';

import { C_ADMIN_USERS } from '../../constants/collections';
export type AdminRole = 'owner' | 'ceo' | 'builder' | 'medical_director' | 'auditor';

export interface AdminUser {
  email: string;
  role: AdminRole;
  name: string;
  createdAt: string;
}

/** Sign in with email/password. Returns the Firebase User on success. */
export async function signInAdmin(email: string, password: string): Promise<User> {
  const cred = await signInWithEmailAndPassword(auth, email, password);
  return cred.user;
}

/** Sign out the current admin user. */
export async function signOutAdmin(): Promise<void> {
  await signOut(auth);
}

/** Check if a UID exists in the admin_users collection. Returns the AdminUser or null. */
export async function checkAdminRole(uid: string): Promise<AdminUser | null> {
  const snap = await getDoc(doc(db, C_ADMIN_USERS, uid));
  if (!snap.exists()) return null;
  return snap.data() as AdminUser;
}

/** Get the current Firebase Auth user (synchronous snapshot). */
export function getCurrentAuthUser(): User | null {
  return auth.currentUser;
}

/** Subscribe to auth state changes. Returns unsubscribe function. */
export function onAdminAuthStateChanged(
  callback: (user: User | null) => void,
): () => void {
  return onAuthStateChanged(auth, callback);
}
